import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon, XIcon } from 'lucide-react';
import { btnIcon } from '../ui/styles';
import { readLocalJson, writeLocalJson } from '../../core/storage';
import { shiftDate, today } from '../../core/format/dates';
import { ease } from '../../core/format/motion';
import { ADMIN_BASE } from '../../utils/hosts';
import type { Signal } from '../../core/types/insights';

/** Een weggeklikt signaal blijft zo lang weg. Een voorkeur van dit apparaat, geen gegevens. */
const DISMISS_DAYS = 14;
const KEY = 'bb-insight-dismissed';
const readDismissed = (): Record<string, string> => {
  const all = readLocalJson<Record<string, string>>(KEY, {});
  const now = today();
  return Object.fromEntries(Object.entries(all).filter(([, until]) => typeof until === 'string' && until > now));
};

/** "Wat je deze week kunt doen": signalen in gewone taal, met één knop. */
export function SignalList({
  signals
}: {
  signals: Signal[];
}) {
  const [dismissed, setDismissed] = useState(readDismissed);
  const reduce = useReducedMotion();
  const shown = signals.filter(s => !dismissed[s.id]);
  const hidden = signals.length - shown.length;
  const dismiss = (id: string) => {
    const next = {
      ...dismissed,
      [id]: shiftDate(today(), DISMISS_DAYS)
    };
    setDismissed(next);
    writeLocalJson(KEY, next);
  };
  const restore = () => {
    setDismissed({});
    writeLocalJson(KEY, {});
  };
  if (shown.length === 0) {
    return <p className="py-6 text-[14px] text-muted" data-id="element-2026" data-mp="2kax59vy92dg1">
        Niets bijzonders deze periode. Alles loopt zoals je zou verwachten.
        {hidden > 0 && <>
            {' '}
            <button type="button" onClick={restore} className="text-ink underline underline-offset-2" data-id="element-2027" data-mp="2t8za6ml3tq8l">
              Toon {hidden} weggeklikt
            </button>
          </>}
      </p>;
  }
  return <div data-id="element-2028" data-mp="20ktr049p89av">
      <ul className="divide-y divide-line" data-id="element-2029" data-mp="20l37n18ut43w">
        <AnimatePresence initial={false} data-id="element-2030" data-mp="4iqdd0uzotm9">
          {shown.map(s => <motion.li key={s.id} layout={!reduce} exit={reduce ? {
          opacity: 0
        } : {
          opacity: 0,
          height: 0
        }} transition={{
          duration: 0.18,
          ease
        }} className="flex items-start gap-3 py-4 first:pt-0" data-id="element-2031" data-mp="4gdv0mlbkh8o">
              <span className={`mt-[7px] h-2 w-2 shrink-0 rounded-full ${s.severity === 'let-op' ? 'bg-clay' : 'bg-sage'}`} aria-hidden="true" data-id="element-2032" data-mp="j97cgdk5j7fz" />
              <div className="min-w-0 flex-1" data-id="element-2033" data-mp="3mnbjviatmfbl">
                <p className="text-[15px] leading-snug text-ink" data-id="element-2034" data-mp="3mo3d4mgy40yp">
                  <span className="sr-only" data-id="element-2035" data-mp="3rlv33ay15d09">{s.severity === 'let-op' ? 'Let op: ' : 'Kans: '}</span>
                  {s.text}
                </p>
                {s.detail && <p className="mt-0.5 text-[13px] leading-relaxed text-muted" data-id="element-2036" data-mp="16wp8ilag8ror">{s.detail}</p>}
                {s.action && <Link to={`${ADMIN_BASE}${s.action.to}`} className="mt-2 inline-flex items-center gap-1 text-[13px] text-ink underline-offset-4 hover:text-clay hover:underline" data-id="element-2037" data-mp="1axixzj3jiejr">
                    {s.action.label}
                    <ArrowRightIcon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" data-id="element-2038" data-mp="2rg13wlop5a9r" />
                  </Link>}
              </div>
              <button type="button" onClick={() => dismiss(s.id)} className={btnIcon} aria-label={`"${s.text}" wegklikken voor ${DISMISS_DAYS} dagen`} data-id="element-2039" data-mp="2hp6ghdzps4mq">
                <XIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-2040" data-mp="2creo6iv5qgi0" />
              </button>
            </motion.li>)}
        </AnimatePresence>
      </ul>
      {hidden > 0 && <button type="button" onClick={restore} className="mt-2 text-[12px] text-muted underline underline-offset-2 hover:text-ink" data-id="element-2041" data-mp="23pox5e56tjkr">
          {hidden} weggeklikt, toon ze weer
        </button>}
    </div>;
}